import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useState, type MouseEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  companyLicenseFormQuery,
  companyLicensesQuery,
  isLicensesTab,
  licensesShellQuery,
  ownerLicensesQuery,
  saveCompanyLicense,
  setCompanyLicense,
  setOwnerLicense,
  type CompanyLicenseRow,
  type CustomerRow,
  type LicensesTab,
} from '../../api/settingsLicenses'
import { stamp } from '../../inventoree/format'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import { OwnerLicenseModal } from './OwnerLicenseModal'

/**
 * Settings → Support Licenses: cele doua file din
 * `views/version-3/settings/customers/tabList.php`, sub capul "Support
 * Licenses", si machetele lor din `settings/licenses/{companies,owners}_support/`.
 *
 * Bara are o SINGURA sectiune, deci filele n-au `group` - ca la Inventoree.
 *
 * CE POATE ECRANUL
 * ----------------
 *   Company Licenses  lista, Add / Edit, Enable / Disable
 *   Owner Licenses    lista, Add / Edit, Enable / Disable
 *
 * "Add license" / "Edit license" pe fila de OAMENI s-au mutat pe 12
 * septembrie 2026 (`OwnerLicenseModal`); tichetul cu SKU pe care il deschide
 * scrierea se face acum pe server (vezi `LicensesSettings` in API).
 *
 * Butoanele care scriu se arata numai cand serverul spune `canWrite`
 * (`settings.licenses.edit`). Poarta ramane pe server: ascunderea doar
 * scuteste un 403 la fiecare apasare, exact ce facea `canAccess` in macheta.
 *
 * SPINNERUL ARE UN CAPAT
 * ----------------------
 * Fiecare din cele trei cereri (carcasa si cele doua liste) se termina intr-una
 * din patru stari, niciodata in rotita fara sfarsit: datele, un 403 cu ecranul
 * de "no access", o eroare cu motivul si butonul Retry (`ui/LoadFailed`), sau
 * lista goala cu randul ei. `if (!page.data) return <Spinner />` singur se
 * sprijinea pe presupunerea ca datele VIN: cand capatul de oameni raspundea 500
 * - `ORDER BY ID` ambiguu, vezi `DbalCustomersSource::orderColumn` - `data`
 * ramanea `undefined` pentru totdeauna si ecranul se invartea. Aceeasi
 * hotarare ca la Settings -> Reports, fila Easeedesk.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** moment(x).format('ll') - "Sep 9, 2026", ca in `LicenseUrls` / OwnersSupportModel. */
function shortDay(value: unknown): string {
  const when = stamp(value)
  if (!when) return ''
  return `${MONTHS[when.getMonth()]} ${when.getDate()}, ${when.getFullYear()}`
}

function today(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

/**
 * Insigna de stare, cu ramurile din portal: fara licenta, oprita, activa fara
 * perioada, activa in perioada, expirata.
 */
function LicenseBadge({ disabled, byDateRange, start, end }: { disabled: number | null; byDateRange: number; start: string; end: string }) {
  if (disabled === null) return <span className="label label-default">no license</span>
  if (disabled === 1) return <span className="label label-danger">disabled</span>
  if (byDateRange === 0) return <span className="label label-success">active</span>
  if (start < end && end > today()) return <span className="label label-success">active</span>
  return <span className="label label-warning">expired</span>
}

/**
 * Ce s-a intamplat, spus cu vorbele SERVERULUI cand le are - un refuz vine ca
 * 400 cu mesajul lui ("License type must be one of: ...").
 */
function failed(error: unknown): string {
  return error instanceof ApiError && error.message ? error.message : 'Something went wrong.'
}

export function LicensesSettingsPage({ tab }: { tab: string }) {
  const navigate = useNavigate()
  const shell = useQuery(licensesShellQuery())

  useEffect(() => {
    if (!shell.data) return
    if (tab !== '' && isLicensesTab(tab)) return
    const first = shell.data.tabs[0]
    if (first) void navigate({ to: '/settings/licenses/$tab', params: { tab: first.id } })
  }, [shell.data, tab, navigate])

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) return <LoadFailed what="Support Licenses" error={shell.error} onRetry={() => void shell.refetch()} />
  if (!shell.data) return <Spinner />

  const current = isLicensesTab(tab) ? tab : (shell.data.tabs[0]?.id ?? '')

  return (
    <>
      {/*
        `od-subrail`, si nu `od-module-rail` ca la celelalte meniuri de modul:
        ecranul asta E intr-o zona. `SettingsMenu` prinde `settings/licenses` sub
        intrarea Customers (zona Global), deci `SettingsZone` deseneaza rail-ul
        zonei, iar meniul de aici e AL DOILEA `aside` fix de pe pagina - exact
        situatia alarmelor din `settings_console.css`, sectiunea 17.

        Fara clasa asta cele doua se calcau: masurat in Edge la 1440, rail-ul
        zonei sta la 0-64 si acesta ajungea la 64-128, adica PESTE continut, care
        pornea tot de la 84. Cu ea, meniul sta la 84-354 si continutul incepe la
        354. Si tot de acolo vine si nestrangerea: sectiunea 21 lasa in pace
        orice `.od-subrail`.
      */}
      <aside className="od-dark-sidebar od-subrail">
        <ul className="od-dark-sidebar-nav default-sidebar-nav">
          <li className="menu-header">Support Licenses</li>
          {shell.data.tabs.map((item) => (
            <SidebarTab key={item.id} tab={item} current={current} />
          ))}
          <li className="menu-block-divider"></li>
        </ul>
      </aside>
      <div className="page-content-wrapper">
        {current === 'companies_support' && <CompaniesPanel canWrite={shell.data.canWrite} />}
        {current === 'owners_support' && <OwnersPanel canWrite={shell.data.canWrite} />}
      </div>
    </>
  )
}

function SidebarTab({ tab, current }: { tab: LicensesTab; current: string }) {
  const navigate = useNavigate()
  const active = tab.id === current
  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    void navigate({ to: '/settings/licenses/$tab', params: { tab: tab.id } })
  }
  return (
    <li className={active ? 'menu-tab active' : 'menu-tab'}>
      <a id={tab.id} title={tab.title} href={'/app/settings/licenses/' + tab.id} onClick={onClick}>
        <i className={tab.icon} aria-hidden="true"></i> {tab.label}
      </a>
    </li>
  )
}

/* -------------------------------------------------------------------------
 * Company Licenses (companies_support/index.php + licenseForm.php)
 * ---------------------------------------------------------------------- */

function CompaniesPanel({ canWrite }: { canWrite: boolean }) {
  const query = useQueryClient()
  const page = useQuery(companyLicensesQuery())
  const [editing, setEditing] = useState(0)

  if (page.error instanceof ApiError && page.error.status === 403) {
    return <div className="alert alert-danger">You do not have access to this action.</div>
  }
  if (page.error) return <LoadFailed what="The company licenses" error={page.error} onRetry={() => void page.refetch()} />
  if (!page.data) return <Spinner />

  const reload = () => query.invalidateQueries({ queryKey: ['settings', 'licenses', 'companies'] })

  const flip = (row: CompanyLicenseRow, disabled: boolean) => {
    if (!confirm(disabled ? 'Disable this license?' : 'Enable this license?')) return
    void setCompanyLicense(row.companyId, disabled)
      .then(() => {
        toast(disabled ? 'License disabled.' : 'License enabled.', 'Success')
        void reload()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
  }

  return (
    <div id="content">
      <h4>
        <strong>Companies</strong> support licenses
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage companies <strong>support licenses</strong>
      </label>
      <hr />
      {!page.data.ready ? (
        <div className="alert alert-info">
          <i className="fas fa-info-circle" aria-hidden="true"></i> Support licenses are not set up on this instance.
        </div>
      ) : null}
      <table className="table table-striped table-hover table-bordered" width="100%" id="companies_licenses">
        <thead>
          <tr>
            <th style={{ width: 30 }}></th>
            <th className="a-left">Company</th>
            <th className="a-left">License start</th>
            <th className="a-left">License end</th>
            <th className="a-left">License type</th>
            <th className="a-left">License status</th>
            <th className="a-right" style={{ width: 100 }}>
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {page.data.data.length === 0 ? (
            <tr>
              <td colSpan={7}>
                <div className="alert alert-info">
                  <i className="fas fa-info-circle" aria-hidden="true"></i> No companies here.
                </div>
              </td>
            </tr>
          ) : (
            page.data.data.map((row) => (
              <tr key={row.companyId} id={row.DT_RowId}>
                <td>
                  <center className="center color-blue font-16">
                    <i className="fas fa-building" aria-hidden="true"></i>
                  </center>
                </td>
                <td className="a-left">
                  <strong>{row.companyName}</strong>
                </td>
                <td className="a-left">{row.byDateRange === 1 ? shortDay(row.startLicense) : ''}</td>
                <td className="a-left">{row.byDateRange === 1 ? shortDay(row.endLicense) : ''}</td>
                <td className="a-left">{row.licenseTypeName}</td>
                <td className="a-left">
                  <LicenseBadge disabled={row.disabled} byDateRange={row.byDateRange} start={row.startLicense} end={row.endLicense} />
                </td>
                <td className="a-right">
                  {!canWrite ? null : row.actions.add === 1 ? (
                    <button type="button" className="btn btn-xs btn-success add-company-license" onClick={() => setEditing(row.companyId)}>
                      Add
                    </button>
                  ) : row.actions.disabled === 1 ? (
                    <button type="button" className="btn btn-xs btn-primary enable-company-license" onClick={() => flip(row, false)}>
                      Enable
                    </button>
                  ) : (
                    <>
                      <button type="button" className="btn btn-xs btn-danger disable-company-license" onClick={() => flip(row, true)}>
                        Disable
                      </button>{' '}
                      <button type="button" className="btn btn-xs btn-primary edit-company-license" onClick={() => setEditing(row.companyId)}>
                        Edit
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <CompanyLicenseModal
        companyId={editing}
        onClose={() => setEditing(0)}
        onSaved={() => {
          setEditing(0)
          void reload()
        }}
      />
    </div>
  )
}

/**
 * Fereastra "License" - `companies_support/licenseForm.php`.
 *
 * Tipurile vin de la server, din `it_company_support_license_type`: macheta
 * veche le scria cu mana (1, 2, 3) desi lista citea numele din tabela, deci
 * cele doua se puteau desparti fara ca nimeni sa vada.
 *
 * Cele doua date se cer numai la tipul cu perioada (`byDateRange === 1`) - in
 * macheta, `data-range` inchidea casutele, dar modelul scria oricum ce venea.
 */
function CompanyLicenseModal({ companyId, onClose, onSaved }: { companyId: number; onClose(): void; onSaved(): void }) {
  const form = useQuery(companyLicenseFormQuery(companyId))
  const [type, setType] = useState('')
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')

  useEffect(() => {
    if (!form.data) return
    setType(form.data.license?.licenseType == null ? '' : String(form.data.license.licenseType))
    setStart(form.data.license?.startLicense ?? '')
    setEnd(form.data.license?.endLicense ?? '')
  }, [form.data])

  const types = form.data?.licenseTypes ?? []
  const chosen = types.find((one) => String(one.id) === type)
  const ranged = chosen?.byDateRange === 1

  return (
    <Modal id="editLicenseModal" open={companyId > 0} onClose={onClose} title="License" labelledBy="editLicenseModalLabel">
      {!form.data ? (
        <Spinner />
      ) : (
        <form method="post" id="company_license_form" onSubmit={(e) => e.preventDefault()}>
          <div className="form-group">
            <label className="control-label">License type:</label>
            <select className="form-control change-license" name="license_type" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">Select...</option>
              {types.map((one) => (
                <option key={one.id} value={one.id}>
                  {one.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="control-label">License start:</label>
            <input
              type="date"
              className="form-control license-range"
              name="license_start"
              disabled={!ranged}
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label className="control-label">License end:</label>
            <input
              type="date"
              className="form-control license-range"
              name="license_end"
              disabled={!ranged}
              value={end}
              onChange={(e) => setEnd(e.target.value)}
            />
          </div>
          <div className="form-group aright">
            <button type="button" className="btn btn-default" data-dismiss="modal" onClick={onClose}>
              Close
            </button>{' '}
            <button
              type="button"
              className="btn btn-primary"
              id="saveCompanySupport"
              onClick={() => {
                void saveCompanyLicense(companyId, { license_type: type, license_start: start, license_end: end })
                  .then(() => {
                    toast('License saved.', 'Success')
                    onSaved()
                  })
                  .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
              }}
            >
              Save
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}

/* -------------------------------------------------------------------------
 * Owner Licenses (owners_support/index.php)
 * ---------------------------------------------------------------------- */

function OwnersPanel({ canWrite }: { canWrite: boolean }) {
  const query = useQueryClient()
  const page = useQuery(ownerLicensesQuery())
  const [editing, setEditing] = useState(0)

  if (page.error instanceof ApiError && page.error.status === 403) {
    return <div className="alert alert-danger">You do not have access to this action.</div>
  }
  if (page.error) return <LoadFailed what="The owner licenses" error={page.error} onRetry={() => void page.refetch()} />
  if (!page.data) return <Spinner />

  const flip = (row: CustomerRow, disabled: boolean) => {
    if (!confirm(disabled ? 'Disable this license?' : 'Enable this license?')) return
    void setOwnerLicense(row.ID, disabled)
      .then(() => {
        toast(disabled ? 'License disabled.' : 'License enabled.', 'Success')
        void query.invalidateQueries({ queryKey: ['settings', 'licenses', 'owners'] })
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
  }

  return (
    <div id="content">
      <h4>
        <strong>Owners</strong> support licenses
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage owners <strong>support licenses</strong>
      </label>
      <hr />
      {editing > 0 ? (
        <OwnerLicenseModal
          ownerId={editing}
          onClose={() => setEditing(0)}
          onSaved={() => {
            setEditing(0)
            void query.invalidateQueries({ queryKey: ['settings', 'licenses', 'owners'] })
          }}
        />
      ) : null}
      <table className="table table-striped table-hover table-bordered" width="100%" id="owners_licenses">
        <thead>
          <tr>
            <th style={{ width: 30 }}></th>
            <th className="a-left">Firstname</th>
            <th className="a-left">Lastname</th>
            <th className="a-left">Email</th>
            <th className="a-left">Phone</th>
            <th className="a-left">License start</th>
            <th className="a-left">License end</th>
            <th className="a-left">License type</th>
            <th className="a-left">License status</th>
            <th className="a-right" style={{ width: 100 }}>
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {page.data.data.length === 0 ? (
            <tr>
              <td colSpan={10}>
                <div className="alert alert-info">
                  <i className="fas fa-info-circle" aria-hidden="true"></i> No contacts for this company.
                </div>
              </td>
            </tr>
          ) : (
            page.data.data.map((row) => (
              <tr key={row.ID} id={row.DT_RowId}>
                <td>
                  <center className="center color-blue font-16">
                    <i className="fas fa-user" aria-hidden="true"></i>
                  </center>
                </td>
                <td className="a-left">
                  <strong>{row.firstname}</strong>
                </td>
                <td className="a-left">
                  <strong>{row.lastname}</strong>
                </td>
                <td className="a-left">{row.email}</td>
                <td className="a-left">{row.phone}</td>
                <td className="a-left">{Number(row.by_date_range) > 0 ? shortDay(row.start_license) : ''}</td>
                <td className="a-left">{Number(row.by_date_range) > 0 ? shortDay(row.end_license) : ''}</td>
                <td className="a-left">{row.license_type_name ?? ''}</td>
                <td className="a-left">
                  <LicenseBadge
                    disabled={row.disabled === null || row.disabled === undefined ? null : Number(row.disabled)}
                    byDateRange={Number(row.by_date_range ?? 0)}
                    start={String(row.start_license ?? '')}
                    end={String(row.end_license ?? '')}
                  />
                </td>
                <td className="a-right">
                  {!canWrite ? null : row.actions.add === 1 ? (
                    <button type="button" className="btn btn-xs btn-success add-owner-license" onClick={() => setEditing(row.ID)}>
                      <i className="fas fa-plus" aria-hidden="true"></i> Add license
                    </button>
                  ) : row.actions.disabled === 1 ? (
                    <button type="button" className="btn btn-xs btn-primary enable-owner-license" onClick={() => flip(row, false)}>
                      Enable
                    </button>
                  ) : (
                    <>
                      <button type="button" className="btn btn-xs btn-primary edit-owner-license" onClick={() => setEditing(row.ID)}>
                        <i className="fas fa-pencil-alt" aria-hidden="true"></i> Edit license
                      </button>{' '}
                      <button type="button" className="btn btn-xs btn-danger disable-owner-license" onClick={() => flip(row, true)}>
                        Disable
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}
